"use client";

import { useTranslation } from "react-i18next";

import { getMedicineDetailLine, getMedicineDisplayName } from "@/entities/medicine/lib/medicine-label";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { OverflowText } from "@/shared/ui/overflow-text";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";

type MedicineLabelMedicine = {
  trade_name?: string | null;
  international_name_ru?: string | null;
  international_name?: string | null;
  name?: string | null;
  strength?: string | null;
  dosage_form?: string | null;
  volume?: string | null;
  pack?: string | number | null;
  is_prescription?: boolean | null;
};

type MedicineLabelProps = {
  medicine: MedicineLabelMedicine;
  className?: string;
  nameOverride?: string | null;
  nameSuffix?: React.ReactNode;
};

export function MedicineLabel({ medicine, className, nameOverride, nameSuffix }: MedicineLabelProps) {
  const { t } = useTranslation();
  const resolved = getMedicineDisplayName(medicine);
  // Fall back to the RU international name first when trade/name are both absent (e.g. MedicineVariation).
  const displayName =
    nameOverride ??
    (resolved !== "-" ? resolved : (medicine.international_name_ru ?? medicine.international_name ?? "-"));
  const detailLine = getMedicineDetailLine(medicine);

  return (
    <div className={cn("flex min-w-0 flex-col justify-center leading-tight", className)}>
      <div className="flex min-w-0 items-center gap-1">
        <OverflowText className="text-foreground min-w-0 text-[13px] font-medium capitalize">
          {displayName}
        </OverflowText>
        {medicine.is_prescription ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="h-4 shrink-0 px-1 text-[9px] leading-none"
                title={t("medicines.badges.prescription")}
              >
                {t("medicines.details.rxBadge")}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{t("medicines.badges.prescription")}</TooltipContent>
          </Tooltip>
        ) : null}
        {nameSuffix}
      </div>
      {detailLine ? <span className="text-muted-foreground truncate text-[10px] font-light">{detailLine}</span> : null}
    </div>
  );
}
